/**
 * Asset and network registry. One internal balance per asset; networks are
 * only chosen at deposit and withdrawal time. Fees and limits here are
 * defaults the operator tunes per environment (see apps/api config).
 */

export type ChainFamily = 'evm' | 'tron' | 'ton' | 'solana' | 'bitcoin' | 'litecoin' | 'dogecoin';

export type NetworkId =
  | 'tron'
  | 'ton'
  | 'ethereum'
  | 'bsc'
  | 'solana'
  | 'polygon'
  | 'arbitrum'
  | 'optimism'
  | 'base'
  | 'avalanche'
  | 'bitcoin'
  | 'litecoin'
  | 'dogecoin';

export type AssetCode = 'USDT' | 'TON' | 'BTC' | 'ETH' | 'SOL' | 'BNB' | 'TRX' | 'USDC' | 'NOT' | 'LTC' | 'DOGE';

/** Each asset owns a mineral; the UI maps minerals to colors. */
export type Mineral =
  | 'jade'
  | 'lapis'
  | 'amber'
  | 'amethyst'
  | 'fluorite'
  | 'citrine'
  | 'garnet'
  | 'sapphire'
  | 'rhodonite'
  | 'peridot'
  | 'jasper';

export interface NetworkDef {
  id: NetworkId;
  name: string;
  short: string;
  family: ChainFamily;
  /** Coin that pays network gas (not always a listed asset). */
  gasSymbol: string;
  /** Chain id for EVM networks: [mainnet, testnet]. */
  evmChainId?: [number, number];
  confirmations: number;
  /** Typical minutes until a deposit is credited. */
  minutes: number;
  /** Deposits are attributed by a memo/comment on a shared address. */
  memo: boolean;
  explorer: { tx: string; address: string };
  testExplorer: { tx: string; address: string };
}

export interface AssetNetwork {
  network: NetworkId;
  /** Token standard as users know it: TRC-20, ERC-20, Jetton, native. */
  standard: string;
  /** Mainnet token contract; absent for the network's native coin. */
  contract?: string;
  /** On-chain decimals (may differ from the internal precision). */
  decimals: number;
  minDeposit: string;
  minWithdraw: string;
  withdrawFee: string;
}

export interface AssetDef {
  code: AssetCode;
  name: string;
  /** Internal ledger precision; every amount is rounded down to it. */
  decimals: number;
  mineral: Mineral;
  coingeckoId: string;
  stable: boolean;
  networks: AssetNetwork[];
}

const tronscan = { tx: 'https://tronscan.org/#/transaction/{tx}', address: 'https://tronscan.org/#/address/{address}' };
const etherscan = (host: string) => ({ tx: `https://${host}/tx/{tx}`, address: `https://${host}/address/{address}` });

export const NETWORKS: Record<NetworkId, NetworkDef> = {
  tron: {
    id: 'tron', name: 'TRON', short: 'TRC-20', family: 'tron', gasSymbol: 'TRX',
    confirmations: 19, minutes: 1, memo: false,
    explorer: tronscan,
    testExplorer: { tx: 'https://nile.tronscan.org/#/transaction/{tx}', address: 'https://nile.tronscan.org/#/address/{address}' },
  },
  ton: {
    id: 'ton', name: 'TON', short: 'TON', family: 'ton', gasSymbol: 'TON',
    confirmations: 1, minutes: 1, memo: true,
    explorer: { tx: 'https://tonviewer.com/transaction/{tx}', address: 'https://tonviewer.com/{address}' },
    testExplorer: { tx: 'https://testnet.tonviewer.com/transaction/{tx}', address: 'https://testnet.tonviewer.com/{address}' },
  },
  ethereum: {
    id: 'ethereum', name: 'Ethereum', short: 'ERC-20', family: 'evm', gasSymbol: 'ETH', evmChainId: [1, 11155111],
    confirmations: 12, minutes: 3, memo: false,
    explorer: etherscan('etherscan.io'), testExplorer: etherscan('sepolia.etherscan.io'),
  },
  bsc: {
    id: 'bsc', name: 'BNB Smart Chain', short: 'BEP-20', family: 'evm', gasSymbol: 'BNB', evmChainId: [56, 97],
    confirmations: 15, minutes: 1, memo: false,
    explorer: etherscan('bscscan.com'), testExplorer: etherscan('testnet.bscscan.com'),
  },
  solana: {
    id: 'solana', name: 'Solana', short: 'SPL', family: 'solana', gasSymbol: 'SOL',
    confirmations: 32, minutes: 1, memo: false,
    explorer: { tx: 'https://solscan.io/tx/{tx}', address: 'https://solscan.io/account/{address}' },
    testExplorer: { tx: 'https://solscan.io/tx/{tx}?cluster=devnet', address: 'https://solscan.io/account/{address}?cluster=devnet' },
  },
  polygon: {
    id: 'polygon', name: 'Polygon', short: 'Polygon', family: 'evm', gasSymbol: 'POL', evmChainId: [137, 80002],
    confirmations: 64, minutes: 3, memo: false,
    explorer: etherscan('polygonscan.com'), testExplorer: etherscan('amoy.polygonscan.com'),
  },
  arbitrum: {
    id: 'arbitrum', name: 'Arbitrum One', short: 'Arbitrum', family: 'evm', gasSymbol: 'ETH', evmChainId: [42161, 421614],
    confirmations: 20, minutes: 1, memo: false,
    explorer: etherscan('arbiscan.io'), testExplorer: etherscan('sepolia.arbiscan.io'),
  },
  optimism: {
    id: 'optimism', name: 'OP Mainnet', short: 'Optimism', family: 'evm', gasSymbol: 'ETH', evmChainId: [10, 11155420],
    confirmations: 20, minutes: 1, memo: false,
    explorer: etherscan('optimistic.etherscan.io'), testExplorer: etherscan('sepolia-optimism.etherscan.io'),
  },
  base: {
    id: 'base', name: 'Base', short: 'Base', family: 'evm', gasSymbol: 'ETH', evmChainId: [8453, 84532],
    confirmations: 20, minutes: 1, memo: false,
    explorer: etherscan('basescan.org'), testExplorer: etherscan('sepolia.basescan.org'),
  },
  avalanche: {
    id: 'avalanche', name: 'Avalanche C-Chain', short: 'AVAX C', family: 'evm', gasSymbol: 'AVAX', evmChainId: [43114, 43113],
    confirmations: 12, minutes: 1, memo: false,
    explorer: etherscan('snowtrace.io'), testExplorer: etherscan('testnet.snowtrace.io'),
  },
  bitcoin: {
    id: 'bitcoin', name: 'Bitcoin', short: 'BTC', family: 'bitcoin', gasSymbol: 'BTC',
    confirmations: 2, minutes: 20, memo: false,
    explorer: { tx: 'https://mempool.space/tx/{tx}', address: 'https://mempool.space/address/{address}' },
    testExplorer: { tx: 'https://mempool.space/testnet4/tx/{tx}', address: 'https://mempool.space/testnet4/address/{address}' },
  },
  litecoin: {
    id: 'litecoin', name: 'Litecoin', short: 'LTC', family: 'litecoin', gasSymbol: 'LTC',
    confirmations: 6, minutes: 15, memo: false,
    explorer: { tx: 'https://litecoinspace.org/tx/{tx}', address: 'https://litecoinspace.org/address/{address}' },
    testExplorer: { tx: 'https://litecoinspace.org/testnet/tx/{tx}', address: 'https://litecoinspace.org/testnet/address/{address}' },
  },
  dogecoin: {
    id: 'dogecoin', name: 'Dogecoin', short: 'DOGE', family: 'dogecoin', gasSymbol: 'DOGE',
    confirmations: 6, minutes: 6, memo: false,
    explorer: { tx: 'https://dogechain.info/tx/{tx}', address: 'https://dogechain.info/address/{address}' },
    testExplorer: { tx: 'https://sochain.com/tx/DOGETEST/{tx}', address: 'https://sochain.com/address/DOGETEST/{address}' },
  },
};

export const ASSETS: AssetDef[] = [
  {
    code: 'USDT', name: 'Tether', decimals: 6, mineral: 'jade', coingeckoId: 'tether', stable: true,
    networks: [
      { network: 'tron', standard: 'TRC-20', contract: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', decimals: 6, minDeposit: '1', minWithdraw: '5', withdrawFee: '1' },
      { network: 'ton', standard: 'Jetton', contract: 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs', decimals: 6, minDeposit: '0.5', minWithdraw: '1', withdrawFee: '0.2' },
      { network: 'ethereum', standard: 'ERC-20', contract: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6, minDeposit: '10', minWithdraw: '15', withdrawFee: '3' },
      { network: 'bsc', standard: 'BEP-20', contract: '0x55d398326f99059fF775485246999027B3197955', decimals: 18, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.3' },
      { network: 'solana', standard: 'SPL', contract: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.5' },
      { network: 'polygon', standard: 'Polygon', contract: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.2' },
      { network: 'arbitrum', standard: 'Arbitrum', contract: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.3' },
      { network: 'optimism', standard: 'Optimism', contract: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.3' },
      { network: 'avalanche', standard: 'AVAX C', contract: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.3' },
    ],
  },
  {
    code: 'TON', name: 'Toncoin', decimals: 9, mineral: 'lapis', coingeckoId: 'the-open-network', stable: false,
    networks: [{ network: 'ton', standard: 'Native', decimals: 9, minDeposit: '0.1', minWithdraw: '0.2', withdrawFee: '0.02' }],
  },
  {
    code: 'BTC', name: 'Bitcoin', decimals: 8, mineral: 'amber', coingeckoId: 'bitcoin', stable: false,
    networks: [{ network: 'bitcoin', standard: 'Native', decimals: 8, minDeposit: '0.00005', minWithdraw: '0.0001', withdrawFee: '0.00002' }],
  },
  {
    code: 'ETH', name: 'Ethereum', decimals: 9, mineral: 'amethyst', coingeckoId: 'ethereum', stable: false,
    networks: [
      { network: 'ethereum', standard: 'Native', decimals: 18, minDeposit: '0.002', minWithdraw: '0.004', withdrawFee: '0.0008' },
      { network: 'arbitrum', standard: 'Native', decimals: 18, minDeposit: '0.0005', minWithdraw: '0.001', withdrawFee: '0.0001' },
      { network: 'base', standard: 'Native', decimals: 18, minDeposit: '0.0005', minWithdraw: '0.001', withdrawFee: '0.0001' },
      { network: 'optimism', standard: 'Native', decimals: 18, minDeposit: '0.0005', minWithdraw: '0.001', withdrawFee: '0.0001' },
    ],
  },
  {
    code: 'SOL', name: 'Solana', decimals: 9, mineral: 'fluorite', coingeckoId: 'solana', stable: false,
    networks: [{ network: 'solana', standard: 'Native', decimals: 9, minDeposit: '0.01', minWithdraw: '0.02', withdrawFee: '0.001' }],
  },
  {
    code: 'BNB', name: 'BNB', decimals: 9, mineral: 'citrine', coingeckoId: 'binancecoin', stable: false,
    networks: [{ network: 'bsc', standard: 'Native', decimals: 18, minDeposit: '0.005', minWithdraw: '0.01', withdrawFee: '0.0005' }],
  },
  {
    code: 'TRX', name: 'TRON', decimals: 6, mineral: 'garnet', coingeckoId: 'tron', stable: false,
    networks: [{ network: 'tron', standard: 'Native', decimals: 6, minDeposit: '5', minWithdraw: '10', withdrawFee: '1' }],
  },
  {
    code: 'USDC', name: 'USD Coin', decimals: 6, mineral: 'sapphire', coingeckoId: 'usd-coin', stable: true,
    networks: [
      { network: 'ethereum', standard: 'ERC-20', contract: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6, minDeposit: '10', minWithdraw: '15', withdrawFee: '3' },
      { network: 'solana', standard: 'SPL', contract: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.5' },
      { network: 'base', standard: 'Base', contract: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.2' },
      { network: 'polygon', standard: 'Polygon', contract: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.2' },
      { network: 'arbitrum', standard: 'Arbitrum', contract: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', decimals: 6, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.3' },
      { network: 'bsc', standard: 'BEP-20', contract: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18, minDeposit: '1', minWithdraw: '2', withdrawFee: '0.3' },
    ],
  },
  {
    code: 'NOT', name: 'Notcoin', decimals: 9, mineral: 'rhodonite', coingeckoId: 'notcoin', stable: false,
    networks: [{ network: 'ton', standard: 'Jetton', contract: 'EQAvlWFDxGF2lXm67y4yzC17wYKD9A0guwPkMs1gOsM__NOT', decimals: 9, minDeposit: '100', minWithdraw: '200', withdrawFee: '20' }],
  },
  {
    code: 'LTC', name: 'Litecoin', decimals: 8, mineral: 'peridot', coingeckoId: 'litecoin', stable: false,
    networks: [{ network: 'litecoin', standard: 'Native', decimals: 8, minDeposit: '0.01', minWithdraw: '0.02', withdrawFee: '0.001' }],
  },
  {
    code: 'DOGE', name: 'Dogecoin', decimals: 8, mineral: 'jasper', coingeckoId: 'dogecoin', stable: false,
    networks: [{ network: 'dogecoin', standard: 'Native', decimals: 8, minDeposit: '10', minWithdraw: '20', withdrawFee: '2' }],
  },
];

export const ASSET_CODES = ASSETS.map((a) => a.code);

const assetIndex = new Map(ASSETS.map((a) => [a.code, a]));

export function getAsset(code: string): AssetDef | undefined {
  return assetIndex.get(code as AssetCode);
}

export function getAssetNetwork(code: string, network: string): AssetNetwork | undefined {
  return getAsset(code)?.networks.find((n) => n.network === network);
}

export function isAssetCode(code: string): code is AssetCode {
  return assetIndex.has(code as AssetCode);
}

export function isNetworkId(id: string): id is NetworkId {
  return id in NETWORKS;
}

/** Every (asset, network) pair served by one chain family. */
export function pairsForFamily(family: ChainFamily): Array<{ asset: AssetDef; net: AssetNetwork; network: NetworkDef }> {
  const out: Array<{ asset: AssetDef; net: AssetNetwork; network: NetworkDef }> = [];
  for (const asset of ASSETS) {
    for (const net of asset.networks) {
      const network = NETWORKS[net.network];
      if (network.family === family) out.push({ asset, net, network });
    }
  }
  return out;
}

export function explorerUrl(network: NetworkId, kind: 'tx' | 'address', value: string, testnet: boolean): string {
  const def = NETWORKS[network];
  const tpl = (testnet ? def.testExplorer : def.explorer)[kind];
  return tpl.replace(kind === 'tx' ? '{tx}' : '{address}', value);
}
